import { BufferAttribute, BufferGeometry, Vector3 } from 'three';

/**
 * Sweep a 2-D profile along a helix using an *architectural* frame: the
 * section's x axis points away from the stair's axis and its y axis points
 * straight up. A Frenet frame would twist the handrail into a corkscrew; this
 * keeps every moulding level, the way a mason would cut it.
 *
 * @param {import('./spiral.js').Helix} helix
 * @param {object} o
 * @param {Array<[number, number]>} o.profile closed polygon, CCW, in (radial, vertical)
 * @param {number} o.radius   distance of the profile origin from the axis
 * @param {number} [o.yOffset]
 * @param {number} [o.steps]
 * @param {[number, number]} [o.range] sub-range of the helix to sweep
 * @param {boolean} [o.caps]
 */
export function sweepHelix(
  helix,
  { profile, radius, yOffset = 0, steps = 1200, range = [0, 1], caps = true },
) {
  const n = profile.length;
  const rings = steps + 1;
  const vertCount = rings * n;
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  const uvs = new Float32Array(vertCount * 2);
  const indices = [];

  // Outward 2-D normal of each profile edge midpoint, averaged per vertex.
  const vertNormals = profileNormals(profile);

  const p = new Vector3();
  const er = new Vector3();
  const [t0, t1] = range;

  for (let i = 0; i < rings; i++) {
    const s = i / steps;
    const t = t0 + (t1 - t0) * s;
    const a = helix.angleAt(t);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    er.set(cos, 0, sin);
    const y = helix.heightAt(t) + yOffset;

    for (let j = 0; j < n; j++) {
      const [px, py] = profile[j];
      const r = radius + px;
      p.set(cos * r, y + py, sin * r);
      const o = (i * n + j) * 3;
      positions[o] = p.x;
      positions[o + 1] = p.y;
      positions[o + 2] = p.z;

      const [nx, ny] = vertNormals[j];
      normals[o] = er.x * nx;
      normals[o + 1] = ny;
      normals[o + 2] = er.z * nx;

      const u = (i * n + j) * 2;
      uvs[u] = s * 48;
      uvs[u + 1] = j / n;
    }
  }

  for (let i = 0; i < steps; i++) {
    for (let j = 0; j < n; j++) {
      const jn = (j + 1) % n;
      const a0 = i * n + j;
      const b0 = i * n + jn;
      const a1 = (i + 1) * n + j;
      const b1 = (i + 1) * n + jn;
      indices.push(a0, a1, b0, b0, a1, b1);
    }
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  geo.setAttribute('normal', new BufferAttribute(normals, 3));
  geo.setAttribute('uv', new BufferAttribute(uvs, 2));
  geo.setIndex(indices);

  if (caps) {
    // Fan-cap both ends so the moulding does not read as hollow when the
    // traveller looks back up the stair.
    const extra = [];
    for (let j = 1; j < n - 1; j++) {
      extra.push(0, j + 1, j);
      const base = steps * n;
      extra.push(base, base + j, base + j + 1);
    }
    geo.setIndex([...geo.index.array, ...extra]);
  }

  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  return geo;
}

function profileNormals(profile) {
  const n = profile.length;
  const out = [];
  for (let j = 0; j < n; j++) {
    const prev = profile[(j - 1 + n) % n];
    const cur = profile[j];
    const next = profile[(j + 1) % n];
    const e1 = [cur[0] - prev[0], cur[1] - prev[1]];
    const e2 = [next[0] - cur[0], next[1] - cur[1]];
    // Right-hand normal of each edge, averaged.
    const n1 = [e1[1], -e1[0]];
    const n2 = [e2[1], -e2[0]];
    let nx = n1[0] + n2[0];
    let ny = n1[1] + n2[1];
    const len = Math.hypot(nx, ny) || 1;
    out.push([nx / len, ny / len]);
  }
  return out;
}

/** A regular polygon profile, for rails and ropes. */
export function circleProfile(radius, sides = 10, squash = 1) {
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2;
    pts.push([Math.cos(a) * radius, Math.sin(a) * radius * squash]);
  }
  return pts;
}

/** A rectangle, optionally chamfered, for skirts and stringers. */
export function rectProfile(width, height, chamfer = 0) {
  const w = width / 2;
  const h = height / 2;
  if (chamfer <= 0) {
    return [
      [-w, -h],
      [w, -h],
      [w, h],
      [-w, h],
    ];
  }
  const c = Math.min(chamfer, w, h);
  return [
    [-w + c, -h],
    [w - c, -h],
    [w, -h + c],
    [w, h - c],
    [w - c, h],
    [-w + c, h],
    [-w, h - c],
    [-w, -h + c],
  ];
}
